import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ColorToken } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import { filterCareerPathSuggestionsByScoreSection } from '@backrs_ui/grow-ui/src/utils/careerPath/careerPathSuggestions';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { CareerPathPill } from './CareerPathPill';
import { FIT_GROUPS } from './fitGroups';

const DEFAULT_VISIBLE_PILLS = 12;
const STRINGS = appStrings.modules.survey.output.blueprintSection.careerPaths.detail;

type CareerPathRankingsProps = Readonly<{
  careerPathSuggestions: CareerPathWithScore[];
  isShared?: boolean;
}>;

export function CareerPathRankings({
  careerPathSuggestions,
  isShared = false,
}: CareerPathRankingsProps) {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const [showAll, setShowAll] = useState(false);

  const viewAllRoute =
    isShared && uuid
      ? APP_ROUTE.SHARED_CAREER_PATHS.replace(':uuid', uuid)
      : APP_ROUTE.CAREER_PATHS;

  // Build groups and count total pills to decide if toggle is needed
  const groups = FIT_GROUPS.map(({ scoreSection, label, dotColor }) => ({
    scoreSection,
    label,
    dotColor,
    paths: filterCareerPathSuggestionsByScoreSection(careerPathSuggestions, scoreSection),
  })).filter((g) => g.paths.length > 0);

  const anyGroupExceedsLimit = groups.some((g) => g.paths.length > DEFAULT_VISIBLE_PILLS);

  const visibleGroups = groups.map((group) => ({
    ...group,
    visiblePaths: showAll ? group.paths : group.paths.slice(0, DEFAULT_VISIBLE_PILLS),
  }));

  return (
    <Box sx={containerSx}>
      <Box sx={headerSx}>
        <Typography variant="display-xs-semibold">{STRINGS.heading}</Typography>
        <Box sx={headerActionsSx}>
          <Button
            id="career-paths-detail-view-btn"
            hierarchy="Secondary gray"
            size="md"
            onClick={() => navigate(viewAllRoute)}
          >
            {STRINGS.viewCta}
          </Button>
          <LubavButton
            id="career-paths-detail-discuss-btn"
            label="Discuss"
            chatMessage="Discuss my career paths"
            promptButtonId="blueprint_career_paths"
          />
        </Box>
      </Box>

      {/* Fit level groups in columns */}
      <Box sx={groupsContainerSx}>
        {visibleGroups.map(({ scoreSection, label, dotColor, visiblePaths }) => {
          if (visiblePaths.length === 0) return null;

          return (
            <Box key={scoreSection} sx={groupSx}>
              <Box sx={groupHeaderSx}>
                <Box sx={dotSx(dotColor)} />
                <Typography variant="text-sm-semibold" sx={groupLabelSx}>
                  {label}
                </Typography>
              </Box>
              <Box sx={pillsContainerSx}>
                {visiblePaths.map((path) => (
                  <CareerPathPill
                    key={path.careerPath.id}
                    id={path.careerPath.id}
                    name={path.careerPath.name}
                    scoreSection={path.scoreSection}
                    isShared={isShared}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      {anyGroupExceedsLimit && (
        <Typography
          id="career-path-rankings-toggle"
          variant="text-sm-semibold"
          onClick={() => setShowAll((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowAll((prev) => !prev);
            }
          }}
          role="button"
          tabIndex={0}
          sx={toggleSx}
        >
          {showAll ? STRINGS.showLess : STRINGS.showMore}
        </Typography>
      )}
    </Box>
  );
}

// --- Styles ---

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  flexWrap: 'wrap',
});

const headerActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  flexWrap: 'wrap',
});

const groupsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'flex-start',
  overflow: 'hidden',
});

const groupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  width: { xs: '100%', sm: 0 },
  flex: { sm: '1 1 0' },
  minWidth: 0,
});

const groupHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  px: backrs.spacing['spacing-xl'],
  py: backrs.spacing['spacing-md'],
});

const dotSx =
  (color: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: backrs.colors[color],
    flexShrink: 0,
  });

const groupLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const pillsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
  px: backrs.spacing['spacing-lg'],
});

const toggleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/600'],
  cursor: 'pointer',
  '&:hover': {
    color: backrs.colors['Brand/700'],
  },
});
